import { FC, useEffect, useRef, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';

import './index.css';

type LineChartType = 'speed' | 'mileage';

interface LineChartProps {
  speed?: Record<string, number>;
  mileage?: Record<string, number>;
  type: LineChartType;
  isToday: boolean;
}

interface ChartConfig {
  maxSpeed: number;
  maxMileage: number;
  height: number;
  margin: { top: number; right: number; bottom: number; left: number };
  pointRadius: { hover: number; data: number };
  tooltipOffset: number;
  hoverThreshold: number;
  colors: {
    speed: string;
    mileage: string;
  };
}

// 常數配置
const CHART_CONFIG: ChartConfig = {
  maxSpeed: 120,
  maxMileage: 0,
  height: 200,
  margin: { top: 20, right: 20, bottom: 30, left: 40 },
  pointRadius: { hover: 3, data: 1 },
  tooltipOffset: 10,
  hoverThreshold: 5,
  colors: {
    speed: '#336699',
    mileage: '#FF6633',
  },
}

const dpr = window.devicePixelRatio || 1;

// 工具函數
const fillData = (data: Record<string, number>): Record<string, number> => {
  const sortedKeys = Object.keys(data).sort();
  if (sortedKeys.length === 0) return {};
  
  const firstDate = dayjs(sortedKeys[0]);
  const start = firstDate.startOf('day');
  const end = firstDate.endOf('day');
  const timeMap = new Map(Object.entries(data));
  
  return Object.fromEntries(
    Array.from(
      { length: Math.floor((end.valueOf() - start.valueOf()) / 1000) + 1 },
      (_, i) => {
        const time = start.add(i, 'second');
        const key = time.format('YYYY-MM-DD HH:mm:ss');
        return [key, timeMap.get(key) ?? 0];
      }
    )
  );
};

const LineChart: FC<LineChartProps> = ({ speed, type, mileage, isToday }) => {
  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const animationFrameRef = useRef<number>(0);
  const scalesRef = useRef<{ xScale: d3.ScaleTime<number, number>; yScale: d3.ScaleLinear<number, number> } | null>(null);
  const dataPointsRef = useRef<[number, number][]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // 計算屬性
  const activeColor = useMemo(() => CHART_CONFIG.colors[type], [type]);
  const hoverPointColor = useMemo(() => 
    type === 'speed' ? CHART_CONFIG.colors.mileage : CHART_CONFIG.colors.speed, 
    [type]
  );

  const maxValue = useMemo(() => {
    const data = type === 'speed' ? speed : mileage;
    const currentMax = Math.ceil(Math.max(...Object.values(data ?? {}), 
      type === 'speed' ? CHART_CONFIG.maxSpeed : CHART_CONFIG.maxMileage
    ));

    return currentMax;
  }, [speed, mileage, type]);

  const processedData = useMemo(() => {
    const data = type === 'speed' ? speed : mileage;
    if (!data) return [];
    
    const filledData = fillData(data);
    const getDate = dayjs(Object.keys(filledData)[0]).format('YYYY-MM-DD');
    const now = dayjs().format('HH:mm:ss');
    const currentTime = dayjs(`${getDate} ${now}`);
    
    return Object.entries(filledData)
      .filter(([time]) => !isToday || dayjs(time).isBefore(currentTime))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [speed, mileage, type, isToday]);

  // 繪製函數
  const createScales = useCallback((width: number) => {
    const yScale = d3.scaleLinear()
      .domain([0, maxValue])
      .range([CHART_CONFIG.height - CHART_CONFIG.margin.bottom, CHART_CONFIG.margin.top]);

    const xScale = d3.scaleTime()
      .domain([dayjs().startOf('day').toDate(), dayjs().endOf('day').toDate()])
      .range([CHART_CONFIG.margin.left, width - CHART_CONFIG.margin.right]);

    scalesRef.current = { xScale, yScale };
    return { xScale, yScale };
  }, [maxValue]);

  const drawAxes = useCallback((ctx: CanvasRenderingContext2D, xScale: d3.ScaleTime<number, number>, yScale: d3.ScaleLinear<number, number>, width: number) => {
    ctx.save();
    ctx.strokeStyle = '#ddd';
    ctx.fillStyle = '#999';
    ctx.font = '10px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    // Y軸
    yScale.ticks(5).forEach(tick => {
      const y = yScale(tick);

      ctx.beginPath();
      ctx.moveTo(CHART_CONFIG.margin.left, y);
      ctx.lineTo(width - CHART_CONFIG.margin.right, y);
      ctx.stroke();
      ctx.fillText(tick.toString(), CHART_CONFIG.margin.left - 5, y);
    });

    // X軸
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    xScale.ticks(24).forEach(tick => {
      const x = xScale(tick);
      ctx.fillText(dayjs(tick).format('H:mm'), x, CHART_CONFIG.height - CHART_CONFIG.margin.bottom + 5);
    });

    ctx.restore();
  }, []);

  const drawChart = useCallback((ctx: CanvasRenderingContext2D, width: number, height: number) => {
    if (!scalesRef.current || processedData.length === 0) return;

    const { xScale, yScale } = scalesRef.current;
    ctx.clearRect(0, 0, width, height);

    // 繪製軸線
    drawAxes(ctx, xScale, yScale, width);

    // 繪製數據線
    ctx.beginPath();
    ctx.strokeStyle = activeColor;
    ctx.lineWidth = 1;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    processedData.forEach(([time, value], index) => {
      const x = xScale(dayjs(time).toDate());
      const y = yScale(value);
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // 繪製數據點
    ctx.fillStyle = activeColor;
    processedData.forEach(([time, value]) => {
      const x = xScale(dayjs(time).toDate());
      const y = yScale(value);
      ctx.beginPath();
      ctx.arc(x, y, CHART_CONFIG.pointRadius.data, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [processedData, activeColor, drawAxes]);

  // 事件處理
  const findClosestPoint = useCallback((mouseX: number, mouseY: number) => {
    const points = dataPointsRef.current;
    let minDist = Infinity;
    let closestIndex = -1;

    for (let i = 0; i < points.length; i++) {
      const [x, y] = points[i];
      const dist = Math.sqrt(Math.pow(x - mouseX, 2) + Math.pow(y - mouseY, 2));
      if (dist < minDist) {
        minDist = dist;
        closestIndex = i;
      }
    }

    if (minDist <= CHART_CONFIG.hoverThreshold) {
      return {
        index: closestIndex,
        point: points[closestIndex],
        data: processedData[closestIndex]
      };
    }
    return null;
  }, [processedData]);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!canvasRef.current || !tooltipRef.current || !scalesRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    const closest = findClosestPoint(mouseX, mouseY);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      if (closest) {
        const [x, y] = closest.point;
        const [time, value] = closest.data;

        // 重繪圖表
        const ctx = canvasRef.current!.getContext('2d')!;
        const width = canvasRef.current!.width;
        const height = canvasRef.current!.height;
        ctx.clearRect(0, 0, width, height);
        drawChart(ctx, width, height);
        
        // 繪製 hover 點
        ctx.beginPath();
        ctx.arc(x, y, CHART_CONFIG.pointRadius.hover, 0, Math.PI * 2);
        ctx.fillStyle = 'white';
        ctx.fill();
        ctx.strokeStyle = hoverPointColor;
        ctx.lineWidth = 2;
        ctx.stroke();

        // 更新 tooltip
        d3.select(tooltipRef.current)
          .style('visibility', 'visible')
          .style('left', `${event.pageX + CHART_CONFIG.tooltipOffset}px`)
          .style('top', `${event.pageY - CHART_CONFIG.tooltipOffset}px`)
          .html(`
            <div>時間：${dayjs(time).format('HH:mm:ss')}</div>
            <div>${type === 'speed' ? '速度' : '里程'}：${value} ${type === 'speed' ? 'km/h' : 'km'}</div>
          `);
      } else {
        d3.select(tooltipRef.current).style('visibility', 'hidden');
      }
    });
  }, [findClosestPoint, type, hoverPointColor, drawChart]);

  const handleMouseLeave = useCallback(() => {
    if (!tooltipRef.current || !canvasRef.current) return;

    d3.select(tooltipRef.current).style('visibility', 'hidden');
    
    const ctx = canvasRef.current.getContext('2d')!;
    const width = canvasRef.current.width;
    const height = canvasRef.current.height;
    ctx.clearRect(0, 0, width, height);
    drawChart(ctx, width, height);
  }, [drawChart]);

  // 重新繪製圖表
  const redrawChart = useCallback(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = CHART_CONFIG.height;

    // 更新 canvas 尺寸
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // 重新創建比例尺
    const { xScale, yScale } = createScales(width);

    // 更新數據範圍
    if (processedData.length > 0) {
      const firstTime = dayjs(processedData[0][0]);
      const lastTime = dayjs(processedData[processedData.length - 1][0]);
      xScale.domain([firstTime.toDate(), lastTime.toDate()]);
    }

    // 更新點坐標
    dataPointsRef.current = processedData.map(([time, value]) => [
      xScale(dayjs(time).toDate()),
      yScale(value)
    ]);

    // 重繪圖表
    drawChart(ctx, width, height);
  }, [createScales, processedData, drawChart]);

  // 使用 debounce 包裝重繪函數
  const debouncedRedraw = useMemo(
    () => debounce(() => {
      requestAnimationFrame(redrawChart);
    }, 250),
    [redrawChart]
  );

  // 初始化
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    const container = containerRef.current;

    // 設置 canvas 尺寸
    const width = container.clientWidth;
    const height = CHART_CONFIG.height;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // 創建 tooltip
    if (!tooltipRef.current) {
      const tooltip = d3.select('body')
        .append('div')
        .attr('class', 'speed-tooltip')
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .style('border', '1px solid #ddd')
        .style('border-radius', '4px')
        .style('padding', '8px')
        .style('font-size', '12px')
        .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)')
        .style('pointer-events', 'none')
        .style('will-change', 'transform');

      tooltipRef.current = tooltip.node() as HTMLDivElement;
    }

    const { xScale, yScale } = createScales(width);

    // 更新數據範圍
    if (processedData.length > 0) {
      const firstTime = dayjs(processedData[0][0]);
      const lastTime = dayjs(processedData[processedData.length - 1][0]);
      xScale.domain([firstTime.toDate(), lastTime.toDate()]);
    }

    // 預計算點坐標
    dataPointsRef.current = processedData.map(([time, value]) => [
      xScale(dayjs(time).toDate()),
      yScale(value)
    ]);

    // 繪製圖表
    drawChart(ctx, width, height);

    // 設置 ResizeObserver
    resizeObserverRef.current = new ResizeObserver(debouncedRedraw);
    resizeObserverRef.current.observe(container);

    // 事件監聽
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      debouncedRedraw.cancel();
    };
  }, [maxValue, processedData, createScales, drawChart, handleMouseMove, handleMouseLeave, debouncedRedraw]);

  // 清理
  useEffect(() => {
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };
  }, []);

  const yAxisLabel = useMemo(() => {
    if (type === 'speed') {
      return '車速 (km/hr)';
    }
    return '行走距離 (Km)';
  }, [type]);

  return (
    <div className="speed-line-chart" ref={containerRef}>
      <div className="speed-line-chart__description">
        <p style={{ color: activeColor }}>{yAxisLabel}</p>
      </div>
      <canvas 
        ref={canvasRef}
        style={{ height: CHART_CONFIG.height }}
      />
    </div>
  );
};

export default LineChart;
